/**
 * Recent runs of the browser-only tools (SRT/VTT converter, word counter, quality
 * analyzer, cost calculator), kept in this browser's localStorage.
 *
 * These tools promise "nothing is uploaded", so their history never leaves the device.
 * Storage can be unavailable (private mode, blocked site data), so every access is
 * guarded and degrades to an empty list.
 */

export interface LocalToolRun {
  id: string;
  /** ISO time of the last update. */
  at: string;
  title: string;
  summary: string;
  /** Whatever the tool needs to put its inputs back. Null when too large to keep. */
  state: Record<string, unknown> | null;
}

export const LOCAL_TOOL_RUNS_LIMIT = 10;

/** Per-run cap on the stored inputs, so a long subtitle file cannot fill the quota. */
export const LOCAL_TOOL_RUN_STATE_MAX_CHARS = 200_000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export function localToolRunsKey(slug: string): string {
  return `wisprs:tool-runs:${slug}`;
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function isRun(value: unknown): value is LocalToolRun {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.at === 'string' &&
    typeof v.title === 'string' &&
    typeof v.summary === 'string' &&
    (v.state === null || (typeof v.state === 'object' && !Array.isArray(v.state)))
  );
}

export function readLocalToolRuns(slug: string, storage: StorageLike | null = defaultStorage()): LocalToolRun[] {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(localToolRunsKey(slug)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isRun).slice(0, LOCAL_TOOL_RUNS_LIMIT) : [];
  } catch {
    return [];
  }
}

export function writeLocalToolRuns(
  slug: string,
  runs: LocalToolRun[],
  storage: StorageLike | null = defaultStorage()
): void {
  try {
    storage?.setItem(localToolRunsKey(slug), JSON.stringify(runs.slice(0, LOCAL_TOOL_RUNS_LIMIT)));
  } catch {
    // Quota or blocked storage: history is a convenience, the tool still works.
  }
}

/**
 * Insert or update one run, newest first. Updating an existing id moves it to the top;
 * a state too large to keep is stored as null (listed, not restorable).
 */
export function upsertLocalToolRun(runs: LocalToolRun[], run: LocalToolRun): LocalToolRun[] {
  const size = run.state ? JSON.stringify(run.state).length : 0;
  const kept: LocalToolRun = size > LOCAL_TOOL_RUN_STATE_MAX_CHARS ? { ...run, state: null } : run;
  return [kept, ...runs.filter((r) => r.id !== run.id)].slice(0, LOCAL_TOOL_RUNS_LIMIT);
}

/** A one-line title from pasted text: the first words, trimmed to fit a list row. */
export function snippetTitle(text: string, max = 70): string {
  const line = text.replace(/\s+/g, ' ').trim();
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}
